import env from '../../app/utils/env';
import { rgIDs } from '../../shared/constants/domIDs';
import { minifiedLibHost } from '../../shared/constants/urls';
import { injectScript } from '../../shared/injectScript';
import { Container } from '../../shared/interfaces';
import { cssNamespace } from '../../shared/styles';
import { applyZIndex, createFrame } from '../utils/builder';
import { DEFAULT_SPACING_BOTTOM, Magnetize } from '../utils/magnetize';
import { MagnetizeElise } from '../utils/magnetize-elise';
import './styles.scss';

export function getServices(platform) {
  const services = [
    {
      service: 'schedule',
      platform,
    },
    {
      service: 'availability',
      platform,
    },
    {
      service: 'photos',
      platform,
    },
    {
      service: 'neighborhood',
      platform,
    },
    {
      service: 'requestText',
      platform,
    },
    {
      service: 'contact',
      platform,
    },
  ];

  return services;
}

export function getCompactWidgetActiveServices(listing, platform) {
  const hasKnock = !!(listing?.knock_community_id && listing?.knock_init_key);
  const services = getServices(platform);

  const knockServices = {
    mobile: {
      contact: listing.has_mobile_knock_contact_property,
      schedule: listing.has_mobile_knock_schedule_tour,
      availability: listing.has_mobile_knock_prices_avail,
      photos: listing.has_mobile_knock_photos,
      neighborhood: listing.has_mobile_knock_neighborhood,
      requestText: listing.has_mobile_knock_request_text,
    },
    more: {
      contact: listing.knock_contact_property,
      schedule: listing.knock_schedule_tour,
      availability: listing.knock_prices_avail,
      photos: listing.knock_photos,
      neighborhood: listing.knock_neighborhood,
      requestText: listing.knock_request_text,
    },
    desktop: {
      contact: listing.has_desktop_knock_contact_property,
      schedule: listing.has_desktop_knock_schedule_tour,
      availability: listing.has_desktop_knock_prices_avail,
      photos: listing.has_desktop_knock_photos,
      neighborhood: listing.has_desktop_knock_neighborhood,
      requestText: listing.has_desktop_knock_request_text,
    },
  };
  const rentgrataServices = {
    desktop: {
      schedule: listing.has_schedule_tour,
      hasSpecials: listing.has_specials,
      virtualTour: listing.has_virtual_tour,
      contact: listing.has_specials,
    },
    mobile: {
      contact: listing.has_mobile_contact_property,
      schedule: listing.has_mobile_schedule_tour,
      hideLabels: listing.hides_mobile_feature_labels,
    },
    more: {
      chat: listing.has_chat_with_resident,
      schedule: listing.has_schedule_tour,
      hasSpecials: listing.has_specials,
      virtualTour: listing.has_virtual_tour,
    },
  };

  type Integrations = { [key: string]: { services?: any; moreIcon?: boolean } };

  const integrations: Integrations = {
    desktop: {
      moreIcon: hasKnock && listing.has_desktop_more_icon,
    },
    mobile: {
      moreIcon: hasKnock && listing.has_mobile_more_icon,
    },
    knock: {
      services: knockServices,
    },
    rentgrata: {
      services: rentgrataServices,
    },
  };

  const activeServiceNames = new Set();

  for (const integration of Object.values(integrations)) {
    const serviceData = integration?.services?.[platform];

    if (serviceData) {
      for (const serviceName of Object.keys(serviceData)) {
        if (serviceData[serviceName]) {
          activeServiceNames.add(serviceName);
        }
      }
    }
  }

  const activeServices = services.filter((service) =>
    activeServiceNames.has(service.service)
  );

  return activeServices;
}

export class RGCompact extends Container {
  config: any;
  elId: string;
  element: HTMLDivElement;
  tooltip: HTMLDivElement;
  private magnetize: Magnetize | null = null;

  constructor() {
    super();

    this.config = {};
    this.elId = rgIDs.compact;
    this.element = null;
    this.tooltip = null;
  }

  onShow() {}

  onHide() {}

  unmount() {
    if (this.magnetize) {
      this.magnetize.cleanup();
      this.magnetize = null;
    }
  }

  onOpen() {
    this.element.classList.add('is-hidden');
  }

  onClose() {
    this.element.classList.remove('is-hidden');
  }

  onToggle() {
    this.element.classList.toggle('is-hidden');
  }

  setMargins() {
    this.element.style[this.config.position] = `${this.config.marginX}px`;
    this.element.style.bottom = `${this.config.marginY}px`;
  }

  setZIndex() {
    applyZIndex(this.element, this.config.listing.widget_zindex);
  }

  setHeight(hasNoActionButtons: boolean) {
    this.element.style.height = hasNoActionButtons ? '72px' : '120px';
  }

  shouldRender(config) {
    return ['fixed'].includes(config?.renderAs);
  }

  onTooltipDisplay = (coordinates: [number, number], label: string) => {
    if (!coordinates) {
      this.tooltip.style.display = 'none';

      return;
    }

    if (!this.tooltip) {
      this.tooltip = document.createElement('div');

      this.tooltip.classList.add(`${cssNamespace}tooltip`);
      document.body.appendChild(this.tooltip);
    }

    const widgetWidth = 192;
    const xPosition = widgetWidth + this.config.marginX;
    const yPadding = 10;

    this.tooltip.textContent = label;
    this.tooltip.style[this.config.position] = `${xPosition}px`;
    this.tooltip.style.bottom = `calc(${yPadding}px + var(--rg-magnetize-bottom, ${this.config.marginY}px))`;
    this.tooltip.style.display = 'flex';
  };

  render = async (config): Promise<void> => {
    if (!this.shouldRender(config)) return;

    const platform = config.screen.isMobile ? 'mobile' : 'desktop';
    const activeServices = getCompactWidgetActiveServices(
      config.listing,
      platform
    );
    const hasNoActionButtons = !activeServices.length;

    try {
      this.config = config;
      this.element = await createFrame(this.elId);

      this.setMargins();
      this.setZIndex();
      this.setHeight(hasNoActionButtons);

      await injectScript(
        `${minifiedLibHost[env.environment]}/rg-compact-init.min.js`,
        ''
      );

      window.RGCompactComponent.render(config, this.elId);

      if (config.syncEliseAI) {
        const targetNode = document.body;

        const observerConfig = {
          attributes: true,
          childList: true,
          subtree: true,
        };

        const callback = (_, observer) => {
          const eliseAiWidgetElement = MagnetizeElise.getEliseElement();

          if (eliseAiWidgetElement) {
            observer.disconnect();

            this.magnetize = new MagnetizeElise(this.element, {
              bottom: config.screen.isMobile ? 0 : DEFAULT_SPACING_BOTTOM,
              left: 0,
              right: 0,
            });
            this.magnetize.init();
          }
        };

        const observer = new MutationObserver(callback);

        observer.observe(targetNode, observerConfig);
      } else {
        this.magnetize = new Magnetize(
          document.getElementById('onetrust-banner-sdk'),
          this.element,
          {
            bottom: this.config.marginY,
          }
        );
        this.magnetize.init();
      }
    } catch (error) {
      console.log(error);
      if (this.magnetize) {
        this.magnetize.cleanup();
        this.magnetize = null;
      }
    }
  };
}

window.RGCompact = new RGCompact();
